import { useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import { useQueryClient } from "@tanstack/react-query";

import { useGetCountries } from "@/api/country/useCountryAPI";
import { useGetLanguages } from "@/api/language/useLanguageApi";
import { useTimezonesQuery } from "@/api/timezones/useTimezonesAPI";
import { useCreateOrganizationUser } from "@/api/user/useUserAPI";
import {
  GET_USER_GROUPS_DEFAULT_PARAMS,
  useGetInfiniteUserGroupsAPI
} from "@/api/userGroups/useUserGroupsAPI";
import { CloseIcon } from "@/assets/icons";
import NotificationContent from "@/components/notification/notificationContent";
import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import DropdownMultiSelectApply from "@/components/ui/dropdownMultiSelectApply";
import FormField from "@/components/ui/formField";
import { MESSAGE } from "@/constants/message";
import { EMAIL_REGEX, ONLY_NUMERIC_REGEX } from "@/constants/regex";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import {
  resetSidebarUserSettingPopupDisplay,
  setSidebarUserSettingPopupDisplay
} from "@/store/slice/uiSlice";
import { AddOrganizationUserRequest, UserStatus } from "@/types/user";
import { trimPhoneNumberByCountryCode } from "@/utils/string";

import * as countryCodes from "country-codes-list";
import { Button } from "equator-design-system";

const TEST_ID_PREFIX = "add-user-panel";

export interface FormValues {
  fullname: string;
  phone_number: string;
  email_address: string;
  country_code: string;
  country: string;
  timezones: string;
  languages: string;
  group_ids: string[];
}

const AddUserPanel = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { isMultipleUserGroupAssignmentEnabled } = useAppFeatures();
  const methods = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: { group_ids: [] }
  });
  const {
    watch,
    setValue,
    control,
    register,
    clearErrors,
    resetField,
    setError,
    handleSubmit,
    formState: { isValid, errors }
  } = methods;
  const queryClient = useQueryClient();
  const countryField = watch("country");

  const handleShowAddUserPanel = () => {
    dispatch(
      setSidebarUserSettingPopupDisplay({
        isPanelView: false,
        isPanelAdd: true,
        isPanelEdit: false
      })
    );
  };

  const handleClosePanel = () => {
    dispatch(resetSidebarUserSettingPopupDisplay());
  };

  const { data: countries, isLoading: isGettingCountries } = useGetCountries();

  const { data: timezones, isLoading: isGettingTimezones } = useTimezonesQuery(
    { country_id: countryField },
    { enabled: !!countryField }
  );

  const {
    data: groupPermissions,
    fetchNextPage: fetchNextGroupPermissions,
    isLoading: isGettingGroupPermissions,
    isFetchingNextPage: isGettingGroupPermissionsNextPage,
    hasNextPage: hasNextGroupPermissionsPage
  } = useGetInfiniteUserGroupsAPI(GET_USER_GROUPS_DEFAULT_PARAMS);

  const { mutate: postCreateOrganizationUser, isPending } =
    useCreateOrganizationUser();

  const { showConfirmation } = useConfirmationModal();

  const handleCloseConfirmation = () => {
    showConfirmation({
      title: MESSAGE.SETTING_SAVE_USER_TITLE_CLOSE,
      message: (
        <div className="text-center">
          {MESSAGE.SETTING_SAVE_USER_DESCRIPTION_CLOSE}
        </div>
      ),
      cancelLabel: "Cancel",
      confirmLabel: "Leave",
      onConfirm: () => {
        handleClosePanel();
      }
    });
  };

  const { data: languageOptions, isLoading: isGettingLanguages } =
    useGetLanguages();

  const countriesMapping: IOptionItem[] = useMemo(() => {
    return (
      countries?.map((item) => ({
        label: item.name,
        value: item.id
      })) ?? []
    );
  }, [countries]);

  const timezonesMapping: IOptionItem[] = useMemo(() => {
    return (
      timezones?.map((item) => ({
        label: item.display_name,
        value: item.id
      })) ?? []
    );
  }, [timezones]);

  const groupOptions: IOptionItem[] = useMemo(() => {
    return (groupPermissions?.pages.flatMap((page) => page.data) ?? []).map(
      (item) => ({
        label: item.name,
        description: item.description,
        value: item.id
      })
    );
  }, [groupPermissions]);

  const onSubmit = (data: FormValues) => {
    const userData: AddOrganizationUserRequest = {
      fullname: data.fullname,
      phone_number: `${data.country_code}-${data.phone_number}`,
      email_address: data.email_address.toLowerCase(),
      country_id: data.country,
      timezone_id: data.timezones,
      language_code: data.languages,
      status: "USER_STATUS_NOT_VERIFIED" as UserStatus,
      user_group_ids: data.group_ids.length > 0 ? data.group_ids : null
    };

    postCreateOrganizationUser(
      { userData },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["getUsers"]
          });
          queryClient.invalidateQueries({
            queryKey: ["getUserGroupById"]
          });
          queryClient.invalidateQueries({
            queryKey: ["getUserGroups"]
          });
          queryClient.invalidateQueries({
            queryKey: ["getInfiniteUserGroups"]
          });
          dispatch(resetSidebarUserSettingPopupDisplay());
          showToast({
            id: "create-user-success",
            type: EToastType.SUCCESS,
            message: (
              <NotificationContent
                title={MESSAGE.SETTING_SAVE_USER_TITLE_SUCCESS}
                description={MESSAGE.SETTING_SAVE_USER_DESCRIPTION_SUCCESS}
              />
            )
          });
        },
        onError: (error) => {
          if (error.code === 409) {
            setError("email_address", {
              type: "manual",
              message: MESSAGE.SETTING_ADD_USER_EMAIL_ALREADY_EXISTS
            });
          } else {
            showToast({
              id: "create-user-failed",
              type: EToastType.ERROR,
              message: getDisplayErrorMessage(error)
            });
          }
          handleShowAddUserPanel();
        }
      }
    );
  };
  const countryCode = watch("country_code");

  const phoneCodeOptions: IOptionItem[] = Array.from(
    new Set(
      countryCodes.all().map((country) => `+${country.countryCallingCode}`)
    )
  ).map((code) => ({
    label: code,
    value: code
  }));

  return (
    <div
      className="
        absolute top-4 right-4 z-[9] border-gainsboro bg-white rounded-lg w-[456px] h-[calc(100vh-32px)] overflow-y-auto
        flex flex-col"
    >
      <div className="sticky top-0 bg-white z-10 px-[24px] py-3 shadow-sm">
        <div className="flex flex-row items-center justify-between">
          <h2 className="text-txtHeading font-sans font-bold text-[20px]">
            Add New User
          </h2>
          <div className="flex gap-3">
            <button
              onClick={() => handleCloseConfirmation()}
              className="p-0 bg-transparent border-0"
              aria-label="Close Add New User Panel"
              data-testid={`${TEST_ID_PREFIX}-close-button`}
            >
              <CloseIcon className="fill-dim-gray" />
            </button>
          </div>
        </div>
      </div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mt-3 space-y-5">
            <div className="flex flex-col px-[24px]">
              <span className="font-sans text-sm font-semibold text-txtHeading">
                Personal Information
              </span>
              <div className="mt-3 space-y-3">
                <FormField<FormValues>
                  name="fullname"
                  placeHolder="Full name"
                  register={register}
                  type="text"
                  onClearErrors={(field) => {
                    clearErrors(field as keyof FormValues);
                  }}
                  rules={{
                    required: true
                  }}
                  autoCleanError={false}
                  resetValue={() => {
                    resetField("fullname");
                  }}
                  error={errors.fullname?.message}
                />
                <FormField<FormValues>
                  name="email_address"
                  register={register}
                  placeHolder="Email address"
                  type="text"
                  onClearErrors={(field) => {
                    clearErrors(field as keyof FormValues);
                  }}
                  rules={{
                    required: true,
                    pattern: {
                      value: EMAIL_REGEX,
                      message: MESSAGE.SETTING_ADD_USER_EMAIL_INVALID
                    }
                  }}
                  autoCleanError={true}
                  resetValue={() => {
                    resetField("email_address");
                  }}
                  error={errors.email_address?.message}
                />
                <div className="flex flex-row items-center gap-3">
                  <Controller
                    name="country_code"
                    control={control}
                    defaultValue="+62"
                    rules={{ required: "Country Code is required" }}
                    render={({ field: { onChange, value, onBlur } }) => (
                      <Dropdown
                        className="text-sm w-[135px]"
                        value={
                          phoneCodeOptions?.find((c) => c.value === value) ||
                          null
                        }
                        aria-label="Country Code"
                        data={phoneCodeOptions}
                        placeholder="Country Code"
                        labelBorder=""
                        onSelect={(val) => {
                          onChange(val?.value ?? "");
                          onBlur();
                        }}
                        withSearch
                        filterMatchedSearch
                      />
                    )}
                  />
                  <FormField<FormValues>
                    name="phone_number"
                    register={register}
                    placeHolder="Phone number"
                    autoCleanError={false}
                    type="tel"
                    rules={{
                      required: true
                    }}
                    onBlur={(e) => {
                      // We need to trim the phone number based on the selected country code
                      const regex = trimPhoneNumberByCountryCode(
                        countryCode || "+62"
                      );
                      const value = e.target.value.replace(regex, "");
                      setValue("phone_number", value);
                    }}
                    onChange={(e) => {
                      const value = e.target.value.replace(
                        ONLY_NUMERIC_REGEX,
                        ""
                      );
                      setValue("phone_number", value, {
                        shouldValidate: true
                      });
                    }}
                    onClearErrors={(field) => {
                      clearErrors(field as keyof FormValues);
                    }}
                    resetValue={() => {
                      resetField("phone_number");
                    }}
                    error={errors.phone_number?.message}
                    hasSpacingOnFocus={false}
                  />
                </div>
              </div>
            </div>
            <div className="flex space-y-4 flex-col px-[24px]">
              <span className="font-sans text-sm font-semibold text-txtHeading">
                Country and Languages
              </span>
              <Controller
                name="country"
                control={control}
                rules={{ required: "Country is required" }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <Dropdown
                    value={
                      countriesMapping.find((c) => c.value === value) || null
                    }
                    data={countriesMapping}
                    placeholder="Country"
                    labelBorder="Country"
                    aria-label="Country"
                    onSelect={(val) => {
                      onChange(val?.value ?? "");
                      setValue("timezones", "");
                      onBlur();
                    }}
                    isLoading={isGettingCountries}
                  />
                )}
              />
              <Controller
                name="timezones"
                control={control}
                rules={{ required: "Timezone is required" }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <Dropdown
                    value={
                      timezonesMapping.find((c) => c.value === value) || null
                    }
                    data={timezonesMapping}
                    placeholder="Timezone"
                    labelBorder="Timezone"
                    onSelect={(val) => {
                      onChange(val?.value ?? "");
                      onBlur();
                    }}
                    isLoading={isGettingTimezones}
                  />
                )}
              />

              <Controller
                name="languages"
                control={control}
                rules={{ required: "Language is required" }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <Dropdown
                    value={
                      languageOptions?.find((c) => c.value === value) || null
                    }
                    data={languageOptions ?? []}
                    isLoading={isGettingLanguages}
                    placeholder="Preferred language"
                    labelBorder="Preferred language"
                    onSelect={(val) => {
                      onChange(val?.value ?? "");
                      onBlur();
                    }}
                  />
                )}
              />
            </div>
            <div className="flex space-y-4 flex-col px-[24px]">
              <span className="font-sans text-sm font-semibold text-txtHeading">
                Group
              </span>
              <Controller
                name="group_ids"
                control={control}
                rules={{
                  validate: (value) => value.length > 0 || "Group is required"
                }}
                render={({ field: { onChange, value, onBlur } }) =>
                  isMultipleUserGroupAssignmentEnabled ? (
                    <DropdownMultiSelectApply
                      data={groupOptions}
                      value={groupOptions.filter((option) =>
                        value.includes(option.value)
                      )}
                      placeholder="Group permission"
                      labelBorder="Group permission"
                      isLoading={isGettingGroupPermissions}
                      onSelect={(items) => {
                        onChange(items.map((item) => item.value));
                        onBlur();
                      }}
                      hasMore={hasNextGroupPermissionsPage}
                      isLoadingMore={isGettingGroupPermissionsNextPage}
                      onLoadMore={fetchNextGroupPermissions}
                    />
                  ) : (
                    <Dropdown
                      filterMatchedSearch
                      withSearch
                      value={
                        groupOptions.find((c) => c.value === value[0]) || null
                      }
                      data={groupOptions}
                      placeholder="Select group permission"
                      labelBorder="Group"
                      className="h-[100px]"
                      isLoading={isGettingGroupPermissions}
                      onSelect={(val) => {
                        onChange(val?.value ? [val.value] : []);
                        onBlur();
                      }}
                      hasMore={hasNextGroupPermissionsPage}
                      isLoadingMore={isGettingGroupPermissionsNextPage}
                      onLoadMore={fetchNextGroupPermissions}
                    />
                  )
                }
              />
            </div>
          </div>
          <div className="flex px-4 justify-center gap-2 absolute bottom-0 bg-white w-full py-[20px] rounded-t-none rounded-lg">
            <Button
              variant="secondary"
              className="h-[40px] w-[198px] btn-secondary text-sm !font-bold"
              onClick={handleCloseConfirmation}
              type="button"
              data-testid={`${TEST_ID_PREFIX}-cancel-button`}
            >
              <span>Cancel</span>
            </Button>
            <Button
              className="h-[40px] w-[198px] btn-primary text-sm !font-bold"
              disabled={!isValid || isPending}
              type="submit"
              data-testid={`${TEST_ID_PREFIX}-create-button`}
            >
              <span>Create</span>
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddUserPanel;
